import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import axios from "axios";
import { API_URL } from "const/constants";
import { formatDate, formatDateTime } from "../../utils/portalHelpers";
import {
  PageHeader,
  Panel,
  StatusChip,
  LoadingPanel,
  ErrorPanel,
  chipTone,
} from "../../components/ui";
import { PARTNER_STATUSES, labelFor, toneFor } from "const/referralEnums";
import { partnerQueryKeys } from "../../api/queryKeys";
import { fetchPartnerMe, fetchPartnerTerms, updatePartnerMe } from "../../api/endpoints/me";
import usePartnerQuery from "../../hooks/usePartnerQuery";
import TermsDocument, { termsCaption } from "../../terms/TermsDocument";
import useAcceptPartnerTerms from "../../terms/useAcceptPartnerTerms";
import { PARTNER_FIELD_MAX_LENGTH } from "const/referralPartnerFields";
import VerificationDocumentsPanel from "./VerificationDocumentsPanel";
import { fcmClient } from "pushNotifications";
import PushNotificationsCard from "pushNotifications/PushNotificationsCard";

const SECTIONS = [
  { id: "profile", label: "Profile" },
  { id: "business", label: "Business" },
  { id: "bank", label: "Bank" },
  { id: "security", label: "Security" },
  { id: "terms", label: "Terms" },
];

const ENTITY_TYPES = [
  { value: "INDIVIDUAL", label: "Individual / Sole trader" },
  { value: "COMPANY", label: "Company" },
  { value: "PARTNERSHIP", label: "Partnership" },
  { value: "TRUST", label: "Trust" },
];

const fieldGrid = "grid grid-cols-1 sm:grid-cols-2 gap-3";

const ReadOnlyField = ({ label, value }) => (
  <div>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 m-0">{label}</p>
    <p className="text-sm text-slate-900 m-0 mt-0.5">{value || "—"}</p>
  </div>
);

const limit = (name) => ({ maxLength: PARTNER_FIELD_MAX_LENGTH[name] });

/** The whole editable surface is one PUT /partner/me, so one form state covers three sections. */
const formFrom = (partner) => ({
  displayName: partner?.displayName || "",
  tradingName: partner?.tradingName || "",
  phone: partner?.phone || "",
  entityType: partner?.entityType || "",
  abn: partner?.abn || "",
  addressLine1: partner?.addressLine1 || "",
  addressLine2: partner?.addressLine2 || "",
  suburb: partner?.suburb || "",
  state: partner?.state || "",
  postcode: partner?.postcode || "",
  country: partner?.country || "Australia",
  bankAccountName: partner?.bankAccountName || "",
  bsb: partner?.bsb || "",
  accountNumber: "",
});

/** The tab lives in the URL, so an alert can open Settings straight on the terms. */
const sectionFrom = (params) => {
  const requested = params.get("tab");
  return SECTIONS.some((item) => item.id === requested) ? requested : SECTIONS[0].id;
};

const SettingsPage = () => {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const section = sectionFrom(params);
  const setSection = (next) => setParams({ tab: next }, { replace: true });
  const [form, setForm] = useState(formFrom(null));
  const [password, setPassword] = useState({ current: "", next: "", confirm: "" });

  const {
    data: partner,
    isLoading,
    isError,
    refetch,
  } = usePartnerQuery(partnerQueryKeys.me(), fetchPartnerMe);

  useEffect(() => {
    if (partner) setForm(formFrom(partner));
  }, [partner]);

  const save = useMutation(updatePartnerMe, {
    onSuccess: () => {
      toast.success("Saved");
      queryClient.invalidateQueries(partnerQueryKeys.me());
    },
    onError: (error) =>
      toast.error(error?.response?.data?.message || "That did not save. Try again."),
  });

  const changePassword = useMutation(
    (payload) => axios.put(`${API_URL}/change-password`, payload),
    {
      onSuccess: () => {
        toast.success("Password updated");
        setPassword({ current: "", next: "", confirm: "" });
      },
      onError: (error) =>
        toast.error(error?.response?.data?.message || "That password change was refused."),
    }
  );

  const patch = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  // An omitted account number must not wipe the stored last-4 — the same bug the admin edit form
  // had (backend 187f48f3). Blank means "leave it alone", so it is dropped from the payload.
  const payload = useMemo(() => {
    const { accountNumber, ...rest } = form;
    return accountNumber.trim() ? { ...rest, accountNumber: accountNumber.trim() } : rest;
  }, [form]);

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Settings" />
        <LoadingPanel label="Loading your profile…" />
      </div>
    );
  }

  if (isError || !partner) {
    return (
      <div>
        <PageHeader title="Settings" />
        <ErrorPanel
          description="The server did not answer. Nothing is lost, try again."
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              Try again
            </button>
          }
        />
      </div>
    );
  }

  const saveButton = (
    <Button
      variant="contained"
      sx={{ bgcolor: "#0f766e" }}
      disabled={save.isLoading}
      onClick={() => save.mutate(payload)}
    >
      {save.isLoading ? "Saving…" : "Save changes"}
    </Button>
  );

  return (
    <div>
      <PageHeader title="Settings" />

      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h2 className="text-lg font-bold text-slate-900 m-0 truncate">{partner.displayName}</h2>
            <StatusChip tone={chipTone(toneFor(PARTNER_STATUSES, partner.status))}>
              {labelFor(PARTNER_STATUSES, partner.status)}
            </StatusChip>
          </div>
          <p className="text-sm text-slate-500 m-0">{partner.email}</p>
          {partner.onboardedAt ? (
            <p className="text-xs text-slate-400 m-0 mt-1">
              Partner since {formatDate(partner.onboardedAt)}
            </p>
          ) : null}
        </div>
      </div>

      <div className="mb-5">
        <PushNotificationsCard platform={fcmClient.platform} />
      </div>

      <Tabs
        value={section}
        onChange={(_, value) => setSection(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        {SECTIONS.map((item) => (
          <Tab key={item.id} value={item.id} label={item.label} />
        ))}
      </Tabs>

      {section === "profile" && (
        <Panel title="Profile" action={saveButton}>
          <div className={fieldGrid}>
            <TextField
              size="small"
              label="Legal / display name"
              value={form.displayName}
              onChange={patch("displayName")}
              inputProps={limit("displayName")}
            />
            <TextField
              size="small"
              label="Trading name"
              value={form.tradingName}
              onChange={patch("tradingName")}
              inputProps={limit("tradingName")}
            />
            <TextField
              size="small"
              label="Phone"
              value={form.phone}
              onChange={patch("phone")}
              inputProps={limit("phone")}
            />
            <TextField
              size="small"
              label="Email"
              value={partner.email || ""}
              disabled
              helperText="Your sign-in address. Ask the office to change it."
            />
          </div>
        </Panel>
      )}

      {section === "business" && (
        <div className="space-y-4">
          <Panel title="Business" action={saveButton}>
            <div className={fieldGrid}>
              <TextField
                size="small"
                select
                label="Entity type"
                value={form.entityType}
                onChange={patch("entityType")}
              >
                {ENTITY_TYPES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="ABN"
                value={form.abn}
                onChange={patch("abn")}
                inputProps={limit("abn")}
              />
              <ReadOnlyField label="TFN (last 4)" value={partner.tfnLast4} />
              <ReadOnlyField label="GST registered" value={partner.gstRegistered ? "Yes" : "No"} />
            </div>
          </Panel>

          <Panel title="Address">
            <div className={fieldGrid}>
              <TextField
                size="small"
                label="Address line 1"
                value={form.addressLine1}
                onChange={patch("addressLine1")}
                inputProps={limit("addressLine1")}
              />
              <TextField
                size="small"
                label="Address line 2"
                value={form.addressLine2}
                onChange={patch("addressLine2")}
                inputProps={limit("addressLine2")}
              />
              <TextField
                size="small"
                label="Suburb"
                value={form.suburb}
                onChange={patch("suburb")}
                inputProps={limit("suburb")}
              />
              <TextField
                size="small"
                label="State"
                value={form.state}
                onChange={patch("state")}
                inputProps={limit("state")}
              />
              <TextField
                size="small"
                label="Postcode"
                value={form.postcode}
                onChange={patch("postcode")}
                inputProps={limit("postcode")}
              />
              <TextField
                size="small"
                label="Country"
                value={form.country}
                onChange={patch("country")}
                inputProps={limit("country")}
              />
            </div>
            <div className="mt-4">{saveButton}</div>
          </Panel>

          <VerificationDocumentsPanel documents={partner.documents} />
        </div>
      )}

      {section === "bank" && (
        <Panel title="Payout details" action={saveButton}>
          <div className={fieldGrid}>
            <TextField
              size="small"
              label="Account name"
              value={form.bankAccountName}
              onChange={patch("bankAccountName")}
              inputProps={limit("bankAccountName")}
            />
            <TextField
              size="small"
              label="BSB"
              value={form.bsb}
              onChange={patch("bsb")}
              inputProps={limit("bsb")}
            />
            <TextField
              size="small"
              label="Account number"
              value={form.accountNumber}
              onChange={patch("accountNumber")}
              inputProps={limit("accountNumber")}
              helperText={
                partner.accountNumberLast4
                  ? `Currently ending ${partner.accountNumberLast4}, leave blank to keep it`
                  : "Only the last four digits are stored"
              }
            />
          </div>
        </Panel>
      )}

      {section === "security" && (
        <Panel title="Password">
          <div className="space-y-3 max-w-md">
            <TextField
              size="small"
              type="password"
              label="Current password"
              fullWidth
              value={password.current}
              onChange={(event) =>
                setPassword((current) => ({ ...current, current: event.target.value }))
              }
            />
            <TextField
              size="small"
              type="password"
              label="New password"
              fullWidth
              value={password.next}
              onChange={(event) =>
                setPassword((current) => ({ ...current, next: event.target.value }))
              }
              helperText="At least 10 characters, with a letter and a symbol"
            />
            <TextField
              size="small"
              type="password"
              label="Confirm new password"
              fullWidth
              value={password.confirm}
              onChange={(event) =>
                setPassword((current) => ({ ...current, confirm: event.target.value }))
              }
            />
            <Button
              variant="contained"
              sx={{ bgcolor: "#0f766e" }}
              disabled={changePassword.isLoading}
              onClick={() => {
                if (password.next.length < 10) {
                  toast.error("Use at least 10 characters");
                  return;
                }
                if (password.next !== password.confirm) {
                  toast.error("Passwords do not match");
                  return;
                }
                changePassword.mutate({
                  currentPassword: password.current,
                  password: password.next,
                });
              }}
            >
              {changePassword.isLoading ? "Updating…" : "Update password"}
            </Button>
          </div>
        </Panel>
      )}

      {section === "terms" && <TermsSection />}
    </div>
  );
};

const TermsDialog = ({ open, onClose, document, heading, acceptedAt }) => (
  <Dialog open={open} onClose={onClose} scroll="paper" fullWidth maxWidth="md">
    <DialogTitle sx={{ pb: 1 }}>
      <span className="block text-base font-semibold text-slate-900">{heading}</span>
      <span className="block text-xs font-normal text-slate-500 mt-1">
        {[document?.title, termsCaption(document)].filter(Boolean).join(" · ")}
        {acceptedAt ? ` · Accepted ${formatDateTime(acceptedAt)}` : ""}
      </span>
    </DialogTitle>
    <DialogContent dividers>
      <TermsDocument document={document} />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

const TermsCard = ({ eyebrow, title, caption, chip, children }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 flex flex-col gap-2">
    <div className="flex items-start justify-between gap-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 m-0">
        {eyebrow}
      </p>
      {chip}
    </div>
    <div>
      <p className="text-sm font-semibold text-slate-900 m-0 break-words">{title}</p>
      {caption ? <p className="text-xs text-slate-500 m-0 mt-0.5">{caption}</p> : null}
    </div>
    {children}
  </div>
);

/**
 * The agreement in force, the version on record for this partner, and a way to re-read exactly
 * what they accepted. The portal's terms gate normally takes care of accepting, so the prompt here
 * only matters if a newer version lands while this page is open.
 */
const TermsSection = () => {
  const [viewing, setViewing] = useState(null);
  const {
    data: terms,
    isLoading,
    isError,
  } = usePartnerQuery(partnerQueryKeys.terms(), fetchPartnerTerms);
  const accept = useAcceptPartnerTerms();

  if (isLoading) return <LoadingPanel label="Loading the agreement…" />;
  if (isError || !terms) {
    return <ErrorPanel title="Couldn't load the agreement" description="Try again in a moment." />;
  }

  const { current, accepted } = terms;
  const sameVersion = !terms.acceptanceRequired;

  return (
    <Panel title="Terms and conditions">
      {terms.acceptanceRequired ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-4">
          <p className="text-sm font-semibold text-amber-900 m-0">
            A newer version of these terms is published
          </p>
          <p className="text-xs text-amber-800 mt-1 mb-3">
            You accepted {terms.acceptedVersion || "no version"}. Version {current?.version} is now
            in force. Read it below and accept to keep referring clients.
          </p>
          <Button
            variant="contained"
            size="small"
            disabled={accept.isLoading}
            onClick={() => accept.mutate()}
          >
            {accept.isLoading ? "Recording…" : `Accept version ${current?.version}`}
          </Button>
        </div>
      ) : null}

      <div className={fieldGrid}>
        <ReadOnlyField label="Version accepted" value={terms.acceptedVersion} />
        <ReadOnlyField label="Accepted on" value={formatDateTime(terms.acceptedAt)} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        <TermsCard
          eyebrow="In force"
          title={current?.title || "Referral partner terms"}
          caption={termsCaption(current)}
          chip={
            <StatusChip tone={sameVersion ? "emerald" : "amber"}>
              {sameVersion ? "Accepted" : "Awaiting acceptance"}
            </StatusChip>
          }
        >
          <div>
            <Button size="small" onClick={() => setViewing("current")}>
              View current terms
            </Button>
          </div>
        </TermsCard>

        <TermsCard
          eyebrow="You accepted"
          title={
            accepted?.title || (terms.acceptedVersion ? "Referral partner terms" : "Nothing yet")
          }
          caption={
            accepted
              ? termsCaption(accepted)
              : terms.acceptedVersion
              ? `Version ${terms.acceptedVersion} is no longer on file`
              : null
          }
        >
          {accepted ? (
            <div>
              <Button size="small" onClick={() => setViewing("accepted")}>
                View accepted terms
              </Button>
            </div>
          ) : null}
        </TermsCard>
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 max-h-[28rem] overflow-y-auto">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 m-0 mb-2">
          {termsCaption(current) || "Current terms"}
        </p>
        <TermsDocument document={current} />
      </div>

      <TermsDialog
        open={viewing === "current"}
        onClose={() => setViewing(null)}
        document={current}
        heading="Terms in force"
      />
      <TermsDialog
        open={viewing === "accepted"}
        onClose={() => setViewing(null)}
        document={accepted}
        heading="The terms you accepted"
        acceptedAt={terms.acceptedAt}
      />
    </Panel>
  );
};

export default SettingsPage;
